import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * JWT payload structure from WorkOS with permissions
 */
interface JWTPayload {
  sub: string;
  org_id?: string;
  role?: string;
  roles?: string[];
  permissions?: string[];
  exp: number;
  iat: number;
}

/**
 * Decode a JWT token without verifying the signature
 * This is safe for client-side use since we just need to read claims
 */
function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    // Handle URL-safe base64 encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = atob(base64);
    return JSON.parse(jsonPayload) as JWTPayload;
  } catch {
    return null;
  }
}

/**
 * Hook to get the current user's permissions
 *
 * WorkOS RBAC includes permissions in the JWT claims as an array.
 * This hook extracts role and permissions from the JWT token.
 *
 * System administrators (role 'system-admin' or 'system_admin') automatically
 * pass all permission checks.
 *
 * Returns:
 * - permissions: Array of permission slugs (e.g., ['app:campaigns:access', 'app:finances:access'])
 * - isLoading: True while authentication state is loading
 * - isSystemAdmin: True if user has system admin role
 * - hasPermission: Check if user has a specific permission (always true for system admins)
 * - hasAnyPermission: Check if user has any of the specified permissions (always true for system admins)
 * - hasAllPermissions: Check if user has all of the specified permissions (always true for system admins)
 */
export function useUserPermissions(): {
  permissions: string[];
  roles: string[];
  isLoading: boolean;
  isSystemAdmin: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
} {
  const { getAccessToken, isLoading: authLoading, role: sdkRole, permissions: sdkPermissions } = useAuth();
  const [jwtRole, setJwtRole] = useState<string | null>(null);
  const [jwtRoles, setJwtRoles] = useState<string[]>([]);
  const [jwtPermissions, setJwtPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;

    (async () => {
      try {
        let token = await getAccessToken();
        if (cancelled) return;

        // Fallback: read from access-token cookie if SDK returns null or token lacks role
        // This is needed for E2E tests where we inject tokens directly
        if (typeof document !== 'undefined') {
          const cookieMatch = document.cookie.match(/access-token=([^;]+)/);
          if (cookieMatch) {
            const cookieToken = cookieMatch[1];
            const cookiePayload = decodeJWT(cookieToken);

            // Prefer cookie token if it has role and SDK token doesn't
            if (cookiePayload?.role && (!token || !decodeJWT(token)?.role)) {
              token = cookieToken;
            }
          }
        }

        if (token) {
          const payload = decodeJWT(token);
          if (payload) {
            setJwtRole(payload.role ?? null);
            setJwtRoles(payload.roles ?? []);
            setJwtPermissions(payload.permissions ?? []);
          }
        }
      } catch (error) {
        console.error('[useUserPermissions] Error decoding JWT:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getAccessToken, authLoading]);

  // Use JWT role first, fall back to SDK role
  const effectiveRole = jwtRole ?? sdkRole;

  // Use JWT permissions if available, otherwise use SDK permissions
  const effectivePermissions = jwtPermissions.length > 0 ? jwtPermissions : sdkPermissions;

  // Multi-role: combine singular role with roles array
  const effectiveRoles = useMemo(() => {
    const all = new Set(jwtRoles);
    if (effectiveRole) all.add(effectiveRole);
    return [...all];
  }, [jwtRoles, effectiveRole]);

  // System admin if any assigned role is an admin alias
  const isSystemAdmin = effectiveRoles.some((role) => role === 'admin' || role === 'system-admin' || role === 'system_admin');

  // Memoize permission and role sets for O(1) lookups
  const permissionSet = useMemo(() => new Set(effectivePermissions), [effectivePermissions]);
  const roleSet = useMemo(() => new Set(effectiveRoles), [effectiveRoles]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (isSystemAdmin) return true;
      return permissionSet.has(permission);
    },
    [permissionSet, isSystemAdmin]
  );

  const hasAnyPermission = useCallback(
    (perms: string[]): boolean => {
      if (isSystemAdmin) return true;
      return perms.some((p) => permissionSet.has(p));
    },
    [permissionSet, isSystemAdmin]
  );

  const hasAllPermissions = useCallback(
    (perms: string[]): boolean => {
      if (isSystemAdmin) return true;
      return perms.every((p) => permissionSet.has(p));
    },
    [permissionSet, isSystemAdmin]
  );

  const hasRole = useCallback(
    (role: string): boolean => {
      if (isSystemAdmin) return true;
      return roleSet.has(role);
    },
    [roleSet, isSystemAdmin]
  );

  const hasAnyRole = useCallback(
    (roles: string[]): boolean => {
      if (isSystemAdmin) return true;
      return roles.some((r) => roleSet.has(r));
    },
    [roleSet, isSystemAdmin]
  );

  return {
    permissions: effectivePermissions,
    roles: effectiveRoles,
    isLoading: authLoading || isLoading,
    isSystemAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
  };
}
